<template>
    <pc-input-group>
        <pc-label
            v-bind:label="label"
            v-bind:for-id="inputId"
            v-bind:show-required="requireRows"
            v-bind:show-help="showHelp"
            v-bind:help-link="showHelpLinkOnly"
            bold
            v-on:help-clicked="emitHelpClicked" />
        <div class="col-12">
            <slot name="help" />
        </div>
        <div
            v-if="datafields == null"
            data-test-role="test-no-table">
            {{ noTableSelected }}
        </div>
        <div v-else>
            <pc-table
                ref="pcTable"
                v-bind:items="tablePlaceholder"
                v-bind:columns="tableColumns"
                v-bind:empty-text="emptyTableText"
                v-bind:hide-actions="readonly"
                v-bind:hide-search="true"
                v-bind:preserve-page="true"
                v-bind:show-actions-per-item="showActionsPerRow"
                display-table-when-empty
                key-prop="index">
                <template
                    v-for="col in datafieldColumns"
                    v-slot:[col.property]="prop"
                    v-bind:key="col.property">
                    <table-item
                        v-bind:datafield="col.datafield"
                        v-bind:is-required="col.showRequired"
                        v-bind:readonly="readonly || prop['index'] <= readonlyPoint"
                        v-bind:model-value="lookUpTableValue(prop, col.property)"
                        v-on:update:model-value="setTableValue(prop, col.property, $event)" />
                </template>
                <template v-slot:actionsRow="prop">
                    <div v-if="!(readonly || prop['index'] <= readonlyPoint)">
                        <pc-button-row>
                            <pc-button v-on:click="removeRow(prop)">
                                {{ removeLabel }}
                            </pc-button>
                            <pc-button
                                v-if="table.length > 1"
                                v-on:click="reorderRow(prop)">
                                {{ reorderLabel }}
                            </pc-button>
                        </pc-button-row>
                    </div>
                </template>
            </pc-table>
            <pc-button-row>
                <pc-button
                    v-bind:disabled="readonly || table.length >= maxTableLength"
                    data-test-role="add-table-item-btn"
                    v-on:click="addEmptyTableItem">
                    {{ addLabel }}
                </pc-button>
                <pc-button
                    v-if="allowTableImport"
                    v-bind:disabled="readonly"
                    v-on:click="importNewRows">
                    {{ importNewRowsButtonLabel }}
                </pc-button>
            </pc-button-row>
        </div>
    </pc-input-group>
</template>

<script>
    import base from "./_elementBase";
    import { PROJECT_TYPES } from "SharedStores/Project/projectStore";
    import { PROJECT_FORM_TYPES } from "Forms/Components/ProjectForm/projectFormStore";
    import { PROJECT_FORM_CONTROLLER_NAMESPACE } from "../ProjectForm/projectFormControllerStore";
    import { isModuleRegistered } from "SharedStores/util";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/table.i18n";
    import { KEYS as COMMON_KEYS, NAMESPACE as COMMON_NAMESPACE } from "I18n/common.i18n";
    import { deepClone } from "Util/util.js";
    import tableItem from "./table/tableItem.vue";
    import { PROGRAM_TYPES } from "SharedStores/Program/programStore";
    import { ELEMENT_PROPERTY_NAME } from "Types/elementTypes";
    import reorderConstructor from "./table/reorderTableDialog.vue";
    import importNewRowsDialog from "./table/importNewRowsDialog.vue";
    import { DIALOG_RESULT } from "Components/pc-dialog.vue";

    const TABLE_ELEMENT_RESULT = { cancel: 1, save: 2 };
    const MAX_TABLE_LENGTH = 100;

    export default {
        components: { "table-item": tableItem },
        extends: base,
        provide() {
            return { tableElement: this };
        },
        data() {
            return {
                // this is a placeholder array for pc-table to track row expansion as the table element data changes
                tablePlaceholder: [],
                maxTableLength: MAX_TABLE_LENGTH
            };
        },
        computed: {
            readonly() {
                return this.baseReadonly || this.isPrefill;
            },
            requiredColumns() {
                const required = this.element.Properties.required;
                if (required == null) {
                    return [];
                }

                return required.split(",");
            },
            addRowOnly() {
                return this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.AddRowOnly);
            },
            requireRows() {
                return this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.RequireRows);
            },
            allowTableImport() {
                return this.getBooleanPropertyValue(ELEMENT_PROPERTY_NAME.AllowTableImport);
            },
            table() {
                return this.getSubElements(this.element, this.$store.getters);
            },
            rowCount() {
                return this.table.length;
            },
            datafields() {
                if (!this.element.GroupDataFields) {
                    return null;
                }
                const visibleFieldMap = {};
                Object.keys(this.element.GroupDataFields).forEach((suffix) => {
                    if (!this.isDataFieldHidden(this.element.GroupDataFields[suffix])) {
                        visibleFieldMap[suffix] = this.element.GroupDataFields[suffix];
                    }
                });
                return visibleFieldMap;
            },
            readonlyPoint() {
                const hasRowCount =
                    this.addRowOnly &&
                    this.projectElement &&
                    this.projectElement.AuxiliaryModel &&
                    this.projectElement.AuxiliaryModel.rowCount;
                let rowCount = 0;
                if (hasRowCount) {
                    rowCount = this.projectElement.AuxiliaryModel.rowCount;
                }
                return rowCount;
            },
            tableColumns() {
                let cols = [];

                cols = cols.concat(this.rowIndexColumn);

                cols = cols.concat(this.datafieldColumns);

                return cols;
            },
            rowIndexColumn() {
                return {
                    header: this.rowHeader,
                    property: "index",
                    orderable: true,
                    searchable: true,
                    visible: true,
                    columnClass: "align-top"
                };
            },
            datafieldColumns() {
                const cols = [];

                for (let i = 0; i < this.element.AuxiliaryModel.datafieldIds.length; i++) {
                    const fieldId = this.element.AuxiliaryModel.datafieldIds[i];
                    const suffix = this.element.AuxiliaryModel.fieldIdToSuffix[fieldId];
                    const datafield = this.$store.getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](fieldId);
                    // Skip over non-multiinstance fields for the time being
                    if (!datafield.IsMultiInstance) {
                        continue;
                    }
                    cols.push({
                        header: suffix,
                        property: fieldId,
                        orderable: false,
                        searchable: true,
                        visible: true,
                        columnClass: "align-top",
                        datafield: datafield,
                        showRequired: this.requiredColumns.includes(fieldId)
                    });
                }
                return cols;
            },
            ...mapI18n(KEYS, NAMESPACE),
            ...mapI18n(COMMON_KEYS, COMMON_NAMESPACE)
        },
        watch: {
            rowCount() {
                this.tablePlaceholder = this.getPlaceHolderTable();
            }
        },
        mounted() {
            this.tablePlaceholder = this.getPlaceHolderTable();
            // there are some cases where there are updates to the
            // table even if we haven't modified any data
            // e.g. hidden columns being unhidden

            this.updateInitialTableValuesIfChanged(this.copyTable());
        },
        methods: {
            /// Update table values. Note the order of the values determines the rows
            updateTableValues(table) {
                for (const suffix in this.datafields) {
                    const items = [];
                    for (let i = 0; i < table.length; i++) {
                        const id = this.datafields[suffix];
                        items.push(table[i][id]);
                    }
                    const dataFieldId = this.datafields[suffix];
                    const value = items;
                    this.sendDataChange(dataFieldId, value);
                }
            },
            updateInitialTableValuesIfChanged(table) {
                if (this.datafields) {
                    let tableNeedsUpdate = false;
                    const suffixes = Object.keys(this.datafields);
                    const firstDatafieldId = this.datafields[suffixes[0]];
                    const multiValues = this.getProjectMultiValue(firstDatafieldId);
                    const maxTableLength = multiValues === null ? 0 : multiValues.length;
                    for (const suffix in this.datafields) {
                        if (this.datafields.hasOwnProperty(suffix)) {
                            const curMultiVals = this.getProjectMultiValue(this.datafields[suffix]);
                            const currentColumnLength = curMultiVals === null ? 0 : curMultiVals.length;
                            if (maxTableLength != currentColumnLength) {
                                // we have columns of different lengths, we need to update the table before being able to save it.
                                tableNeedsUpdate = true;
                            }
                        }
                    }

                    if (tableNeedsUpdate) {
                        this.updateTableValues(table);
                    }
                }
            },
            lookUpTableValue(row, propName) {
                const index = this.getIndexFromRow(row);
                return this.table[index][propName];
            },
            copyTable() {
                return deepClone(this.table);
            },
            addEmptyTableItem() {
                const copyCurrentTableElement = this.copyTable();

                const emptyElement = { index: this.rowCount };
                for (const suffix in this.datafields) {
                    const id = this.datafields[suffix];
                    emptyElement[id] = "";
                }
                copyCurrentTableElement.push(emptyElement);

                this.updateTableValues(copyCurrentTableElement);
            },
            async importNewRows() {
                if (!isModuleRegistered(PROJECT_FORM_CONTROLLER_NAMESPACE, this.$store.state)) return;

                const dialogResult = await this.$app.showDialog({
                    title: this.importNewRowsDialogHeader,
                    customComponent: importNewRowsDialog,
                    customComponentOptions: {
                        elementId: this.element.PublicId,
                        tableItems: this.table,
                        tableColumns: this.tableColumns,
                        maxTableLength: this.maxTableLength
                    },
                    extraLarge: true,
                    customComponentIsValidToCloseHookAsync: "isValidToClose",
                    customComponentResultHook: "dialogResult",
                    buttons: [
                        {
                            text: this.cancelText,
                            result: DIALOG_RESULT.cancel
                        },
                        {
                            text: this.okText,
                            primary: true,
                            result: DIALOG_RESULT.ok
                        }
                    ]
                });

                if (dialogResult.action === DIALOG_RESULT.ok) {
                    for (let i = 0; i < Object.keys(dialogResult.data.TableColumns).length; i++) {
                        const tableColumn = dialogResult.data.TableColumns[i];
                        const dataFieldId = tableColumn.DataFieldId;
                        let multiValues = this.getProjectMultiValue(dataFieldId);
                        multiValues = multiValues.concat(tableColumn.Values);
                        this.sendDataChange(dataFieldId, multiValues);
                    }
                }
            },
            getPlaceHolderTable() {
                if (!this.datafields) {
                    return [];
                }

                const tablePlaceHolder = [];

                // NOTE: if this is refactored please change to 0-indexed
                for (let i = 1; i <= this.rowCount; i++) {
                    const item = { index: i };
                    tablePlaceHolder.push(item);
                }
                return tablePlaceHolder;
            },
            getSubElements(element, getters) {
                if (!this.datafields) {
                    return [];
                }
                const map = {};
                const tableSubElements = [];
                const suffixes = Object.keys(this.datafields);
                const firstDatafieldId = this.datafields[suffixes[0]];
                let multiValues = this.getProjectMultiValue(firstDatafieldId);

                // get longest column, so we don't hide any rows by mistake
                let maxTableLength = multiValues === null ? 0 : multiValues.length;
                for (const suffix in this.datafields) {
                    if (this.datafields.hasOwnProperty(suffix)) {
                        const curMultiVals = this.getProjectMultiValue(this.datafields[suffix]);
                        const currentColumnLength = curMultiVals === null ? 0 : curMultiVals.length;
                        if (maxTableLength < currentColumnLength) {
                            maxTableLength = currentColumnLength;
                            multiValues = curMultiVals;
                        }
                    }
                }
                if (!multiValues) {
                    return [];
                }

                // Get count and create table elements for each item
                for (let i = 0; i < multiValues.length; i++) {
                    tableSubElements.push({ index: i + 1 });
                }

                // Fill in each element
                for (const suffix in this.datafields) {
                    if (this.datafields.hasOwnProperty(suffix)) {
                        const id = this.datafields[suffix];
                        map[suffix] = getters[PROJECT_TYPES.getters.dataFieldMultiValueFromId](id);
                        for (let i = 0; i < multiValues.length; i++) {
                            let mappedValue = map[suffix][i];
                            if (mappedValue == null) {
                                mappedValue = "";
                            }
                            tableSubElements[i][id] = mappedValue;
                        }
                    }
                }

                return tableSubElements;
            },
            setTableValue(row, dataFieldId, value) {
                const index = this.getIndexFromRow(row);
                this.$store.dispatch(
                    PROJECT_FORM_TYPES.actions.setProjectData,
                    { dataFieldId, value, index },
                    { root: true }
                );
            },
            showActionsPerRow(row) {
                return row.index > this.readonlyPoint && !this.readonly;
            },
            removeRow(rowToRemove) {
                const copyCurrentTableElement = this.copyTable();
                const updatedTable = copyCurrentTableElement.filter((row) => row.index != rowToRemove.index);
                this.updateTableValues(updatedTable);
            },
            // Row index is the display row number not the index of the row in the table
            getIndexFromRow(row) {
                return this.getArrayIndexFromRowIndex(row.index);
            },
            getArrayIndexFromRowIndex(index) {
                return index - 1;
            },
            async reorderRow(rowToMove) {
                const updateTableResult = await this.$app.showDialog({
                    buttons: [
                        {
                            text: this.cancelText,
                            primary: false,
                            result: TABLE_ELEMENT_RESULT.cancel,
                            id: "CancelTableElementReorder"
                        },
                        {
                            text: this.okText,
                            primary: true,
                            result: TABLE_ELEMENT_RESULT.save,
                            id: "TableElementReorder"
                        }
                    ],
                    customComponent: reorderConstructor,
                    customComponentOptions: {
                        tableSize: this.table.length,
                        startIndex: this.readonlyPoint + 1,
                        initialIndex: rowToMove.index
                    },
                    customComponentResultHook: "getNewRowNumber",
                    title: this.reorderTableRowDialogHeader
                });

                if (updateTableResult.action == TABLE_ELEMENT_RESULT.cancel) {
                    return;
                }

                this.reorderRowInternal(rowToMove, updateTableResult.data);
            },
            reorderRowInternal(rowToMove, targetNewIndex) {
                const indexToMove = rowToMove.index;
                const copyCurrentTableElement = this.copyTable();
                const updatedTable = copyCurrentTableElement.filter((row) => row.index != indexToMove);
                const tableRow = copyCurrentTableElement.find((row) => row.index == indexToMove);
                updatedTable.splice(this.getArrayIndexFromRowIndex(targetNewIndex), 0, tableRow);
                this.updateTableValues(updatedTable);
            },
            isDataFieldHidden(datafield) {
                const field = this.$store.getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](datafield);
                if (field.IsHidden) {
                    return true;
                }

                return false;
            }
        }
    };
</script>

<style></style>
